<template>
  <div>
    <div
      class="rounded-xl shadow-card"
      :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
    >
      <nav
        class="border-b flex space-x-4 px-1"
        :class="mode === 'dark' ? 'border-dark-50' : ''"
      >
        <div
          class="inline-flex items-center text-[15px] relative p-4 nav-tab cursor-pointer"
          :class="
            activeTab === 'single'
              ? 'active font-medium'
              : mode === 'dark'
              ? 'text-dark-15'
              : 'text-gray-500'
          "
          @click="setActiveTab('single')"
        >
          Single Page
        </div>
        <div
          class="inline-flex items-center text-[15px] relative p-4 nav-tab cursor-pointer"
          :class="
            activeTab === 'bulk'
              ? 'active font-medium'
              : mode === 'dark'
              ? 'text-dark-15'
              : 'text-gray-500'
          "
          @click="setActiveTab('bulk')"
        >
          Bulk Pages
        </div>
      </nav>
      <div v-if="activeTab === 'single'">
        <div
          class="px-4 py-3.5 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <span class="font-medium text-[17px] leading-none"
            >Add in your exact search (EMQ) you want to rank for</span
          >
        </div>
        <div
          class="px-4 py-3.5 border-b grid grid-cols-3 items-center gap-4 md:gap-y-4 md:grid-cols-1 2xl:grid-cols-2 2xl:gap-x-2 2xl:gap-y-3"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <div class="flex-1">
            <label for="keyword" class="text-[15px] mb-1 block">Keyword</label>
            <input
              type="text"
              class="form-field"
              id="keyword"
              placeholder="Enter search keyword, e.g., Pizza Las Vegas"
              v-model="query"
              @blur="fetchDifficulty"
              v-tooltip="{
                content: `<div style='width: 350px'>
                For non-service searches, use an exact match query. For services, enter a service and city (e.g., 'Pizza Las Vegas') and choose a specific location (country, state, city) in the Locations field for targeted results. This method helps boosts accuracy, relevance, and provides localized SERPs based on geolocation.
                </div>`,
                html: true,
                distance: 6,
              }"
            />
          </div>
          <div class="flex-1">
            <label for="location" class="text-[15px] mb-1 block"
              >Location</label
            >
            <Multiselect
              v-model="location"
              placeholder="Search by typing your location"
              label="full_name"
              valueProp="full_name"
              @open="handleOpenOptionList"
              @change="handleLocationChange"
              @clear="onClear"
              :delay="0"
              :searchable="true"
              :options="
                async function (searchQuery) {
                  if (!searchQuery) return await fetchLocations('a');
                  return await fetchLocations(searchQuery);
                }
              "
              object
              class="form-field"
            >
            </Multiselect>
          </div>
          <div class="flex-1">
            <div class="flex items-center mb-1">
              <div class="checkbox">
                <input
                  id="trackPositionRanking"
                  type="checkbox"
                  v-model="trackPositionRanking"
                />
                <label for="trackPositionRanking"
                  >Rank tracker & OnPage score</label
                >
              </div>
            </div>
            <div>
              <input
                class="form-field"
                type="text"
                v-model="trackPositionURL"
                placeholder="Enter URL"
                :disabled="!trackPositionRanking"
              />
            </div>
          </div>
        </div>
        <div v-if="difficult" class="p-4">
          <label class="flex items-center gap-1.5">
            <span class="font-medium">Keyword Difficulty:</span>
            <span
              class="inline-flex items-center text-center h-7 px-2 text-[13px] font-medium rounded-md capitalize"
              :class="getDiffClass(difficult)"
              v-if="
                difficult.avg_difficulty_diff_text &&
                difficult.avg_difficulty_diff
              "
            >
              <span class="mr-1">
                {{ difficult?.avg_difficulty_diff_text }}</span
              >
              <span>
                ({{
                  difficult?.avg_difficulty_diff >= 0
                    ? `+${difficult?.avg_difficulty_diff}`
                    : difficult?.avg_difficulty_diff
                }}%)
              </span>
            </span>
            <span v-else>Processing will be available in reports</span>
          </label>
        </div>
        <div v-if="!difficult && showDifficultyErrorMessage" class="p-4">
          <span class="font-medium">Keyword Difficulty:</span> Processing will
          be available in reports
        </div>
        <div
          class="border-t-4 rounded-bl-xl rounded-br-xl"
          :class="
            mode === 'dark' ? 'border-dark-50 bg-dark-70' : 'bg-white-100'
          "
        >
          <div class="flex items-center px-4 py-3 justify-end gap-2">
            <button
              class="button primary-button"
              @click="showAdvanceOptionsModal = true"
            >
              <Settings :size="20" :stroke-width="1.75" />
              Advanced Options
            </button>
            <button class="button primary-button" @click="serpSubmit">
              <TextSearch :size="20" :stroke-width="1.75" />
              Start Keyword Spy Report
            </button>
          </div>
        </div>
      </div>
      <div v-if="activeTab === 'bulk'">
        <div
          class="px-4 py-3.5 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <span class="font-medium text-[17px] leading-none"
            >Add in your support page search (EMQ) you want to rank for (up to
            10, one search phrase per line)</span
          >
        </div>
        <div
          class="px-4 py-3.5 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <div class="flex items-center justify-between gap-3 xl:flex-col">
            <div class="flex-1 xl:w-full">
              <label for="location" class="text-[15px] mb-1 block"
                >Location</label
              >
              <Multiselect
                class="form-field"
                v-model="location"
                placeholder="Search by typing your location"
                label="full_name"
                valueProp="full_name"
                @open="handleOpenOptionList"
                @change="handleLocationChange"
                :delay="0"
                :searchable="true"
                :options="
                  async function (searchQuery) {
                    if (!searchQuery) return await fetchLocations('a');
                    return await fetchLocations(searchQuery);
                  }
                "
                object
              >
              </Multiselect>
            </div>
            <div class="flex-1 xl:w-full">
              <label class="text-[15px] mb-1 block">Project</label>
              <Multiselect
                class="form-field"
                v-model="selectedProject"
                :options="projects"
                placeholder="Choose project"
                label="name"
                valueProp="uuid"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
          </div>
          <div class="flex-1 mt-3">
            <textarea
              class="form-field h-40"
              rows="10"
              placeholder="Enter keyword / exact match auery per line (max. 10)"
              v-model="bulkQueries"
            ></textarea>
            <div v-if="showSprayAndPrayError && selectedSpeedThreshold !== 0">
              Currently our Bulk keyword reports feature is exclusively
              accessible through our spray & pray method. If you need
              comprehensive keyword testing, consider starting with a single EMQ
              above or get in touch with our sales team to explore our
              enterprise solution further.
            </div>
          </div>
        </div>
        <div
          class="border-t-4 rounded-bl-xl rounded-br-xl"
          :class="
            mode === 'dark' ? 'border-dark-50 bg-dark-70' : 'bg-white-100'
          "
        >
          <div class="flex items-center px-4 py-3 justify-end gap-2">
            <button
              class="button primary-button"
              @click="showAdvanceOptionsModal = true"
            >
              <Settings :size="20" :stroke-width="1.75" />
              Advanced Options
            </button>
            <button class="button primary-button" @click="serpSubmitBulk">
              <TextSearch :size="20" :stroke-width="1.75" />
              Start Bulk Keyword Spy Reports
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="showAdvanceOptionsModal"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="max-w-2xl rounded-xl w-full mx-auto overflow-hidden"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            Advance search options
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="showAdvanceOptionsModal = false"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="grid grid-cols-2 gap-4 xs:grid-cols-1">
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="googleDomain"
                :options="domainDetailsList"
                @searchChange="(q, m) => getDomainDetailsList('', m.options)"
                placeholder="Choose google domain"
                :searchable="true"
                :canDeselect="true"
                label="domain"
                valueProp="domain"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="googleCountry"
                :options="countries"
                placeholder="Choose country"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="googleLanguage"
                :options="languages"
                placeholder="Choose language"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div class="flex-1">
              <Multiselect
                class="form-field"
                v-model="selectedProject"
                :options="projects"
                placeholder="Choose project"
                label="name"
                valueProp="uuid"
                :searchable="true"
                :canDeselect="true"
              ></Multiselect>
            </div>
            <div
              class="col-span-2 border rounded-md xs:col-span-1"
              :class="
                mode === 'dark'
                  ? 'border-dark-50'
                  : 'bg-white-100 border-gray-30 border-opacity-50'
              "
            >
              <div
                class="p-4 border-b"
                :class="mode === 'dark' ? 'border-dark-50' : ''"
              >
                <h6 class="font-medium leading-none">
                  AI models/testing options
                </h6>
              </div>
              <div class="grid grid-cols-2 gap-4 xs:grid-cols-1 p-4">
                <template v-for="(test, index) in performTest" :key="index">
                  <div
                    class="h-11 border rounded-md items-center px-3"
                    :class="[
                      mode === 'dark'
                        ? 'border-dark-50 bg-dark-70'
                        : 'border-gray-30 border-opacity-50 bg-gray-20 bg-opacity-10',
                      test.label === 'Neural Matching Test' ? 'hidden' : 'flex',
                    ]"
                  >
                    <div class="checkbox">
                      <input
                        :disabled="test.disable"
                        type="checkbox"
                        :id="test.key"
                        :checked="test.value"
                        :value="test.value"
                        v-model="test.value"
                      />
                      <label :for="test.key">{{ test.label }}</label>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="isShowWizardModalAiArticleCoPilot"
      class="bg-opacity-40 fixed inset-0 z-[9999] flex items-center justify-center p-4"
      :class="mode === 'dark' ? 'bg-white-100' : 'bg-black-100'"
    >
      <div
        class="max-w-3xl rounded-xl w-full mx-auto"
        :class="mode === 'dark' ? 'bg-dark-70' : 'bg-white-100'"
      >
        <div
          class="flex items-center justify-between p-4 border-b"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <h5 class="font-medium text-[17px] leading-none">
            High quality article (HQA) V2.1
          </h5>
          <X
            :size="22"
            :stroke-width="1.75"
            @click="handleCancel"
            class="cursor-pointer"
          />
        </div>
        <div
          class="without-scrollbar max-h-modal overflow-y-auto p-4"
          :class="mode === 'dark' ? 'bg-dark-70' : 'bg-gray-20 bg-opacity-30'"
        >
          <div class="grid grid-cols-3 gap-4">
            <div class="col-span-3">
              <label for="topic" class="text-[15px] block mb-1"
                >Topic of Article: (AI Assisted)</label
              >
              <input
                type="text"
                v-model="topicOfArticle"
                placeholder="Topic"
                class="form-field"
                id="topic"
                required
              />
            </div>
            <div class="col-span-3">
              <label for="match-ai" class="text-[15px] block mb-1"
                >Match AI Analysis Competitor Page Type: (Recommended)</label
              >
              <input
                type="text"
                v-model="instructionsOfArticle"
                placeholder="Instructions"
                class="form-field"
                id="match-ai"
              />
            </div>
            <div class="col-span-3 flex items-center gap-4">
              <div
                class="h-11 border rounded-md flex items-center px-4 flex-1"
                :class="[
                  mode === 'dark'
                    ? 'bg-dark-70 border-dark-50'
                    : 'border-gray-30 border-opacity-50 bg-white-100',
                ]"
              >
                <div class="checkbox">
                  <input
                    type="checkbox"
                    value="url"
                    id="useCustomKnowledgeBase"
                    v-model="useCustomKnowledgeBase"
                  />
                  <label for="useCustomKnowledgeBase"
                    >Use custom knowledge base</label
                  >
                </div>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="url"
                  id="genURL"
                  v-model="articleSelectedPrompts"
                />
                <label for="genURL">URL</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="title"
                  id="genTitle"
                  v-model="articleSelectedPrompts"
                />
                <label for="genTitle">Title</label>
              </div>
            </div>
            <div
              class="h-11 border rounded-md flex items-center px-4"
              :class="
                mode === 'dark'
                  ? 'bg-dark-70 border-dark-50'
                  : 'border-gray-30 border-opacity-50 bg-white-100'
              "
            >
              <div class="checkbox">
                <input
                  type="checkbox"
                  value="md"
                  id="genMD"
                  v-model="articleSelectedPrompts"
                />
                <label for="genMD">Meta description</label>
              </div>
            </div>
            <div class="col-span-3">
              <div
                class="rounded-lg px-4 py-2 flex gap-2"
                :class="mode === 'dark' ? 'bg-warning-100' : 'bg-warning-50'"
              >
                <div class="flex-1">
                  <span class="text-[15px] text-opacity-80 text-black-100"
                    >Please be aware that by proceeding with the upcoming
                    action, your current content in the Article Builder will be
                    permanently replaced.</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-end p-4 border-t space-x-2"
          :class="mode === 'dark' ? 'border-dark-50' : ''"
        >
          <button @click="handleContinue" class="button primary-button">
            <CheckCheck :size="20" :stroke-width="1.75" />
            Continue
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from "lodash";
import { ref, onMounted } from "vue";
import type { Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import Multiselect from "@vueform/multiselect";
import { useToast } from "vue-toast-notification";
import helper from "../libs/helper";
import { useUserStore } from "../stores/user";
import ApiRequest from "../libs/ApiRequest";
import { CreateSearch, PostSearchData } from "../libs/types";
import { Settings, TextSearch, X, CheckCheck } from "lucide-vue-next";
interface Props {
  type: number;
  mode: string;
}
const isAiArticleCoPilot = ref(false);
const isShowWizardModalAiArticleCoPilot = ref(false);
const articleSelectedPrompts: Ref<string[]> = ref([]);
const activeTab = ref("single");
const topicOfArticle = ref(
  "Use AI Detection for High Quality Article Or Override here with written instructions"
);
const instructionsOfArticle = ref(
  "Use AI Detection for High Quality Article Or Override here with written instructions"
);
const useCustomKnowledgeBase = ref(false);
const performTest = ref([
  {
    label: "BERT test",
    value: true,
    key: "bert_test1",
    disable: true,
  },
  {
    label: "RankBrain test",
    value: true,
    key: "perform_rankbrain_test",
    disable: false,
  },
  {
    label: "TW-BERT test",
    value: true,
    key: "perform_tw_bert_test",
    disable: false,
  },
  {
    label: "Query interpretation test",
    value: true,
    key: "perform_query_interpt_test",
    disable: false,
  },
]);
const emit = defineEmits(["submit"]);
const userStore = useUserStore();
const props = defineProps<Props>();
const query = ref("");
const bulkQueries = ref("");
const limit = ref(100);
const location = ref(null);
const googleCountry = ref("");
const googleDomain = ref("");
const googleLanguage = ref("");
const countries = ref([]);
const domainDetails = ref([]);
const domainDetailsList = ref([]);
const languages = ref([]);
const selectedProject = ref(null);
const projects = ref([]);
const trackPositionRanking = ref(false);
const trackPositionURL = ref("");
const showAdvanceOptionsModal = ref(false);
const selectedStopwords = ref([]);
const showStopwords = ref(true);
const showSprayAndPrayError = ref(false);
const selectedSpeedThreshold = ref(0.35);
const difficult = ref(null);
const showDifficultyErrorMessage = ref(false);
const route = useRoute();
const router = useRouter();
const $toast = useToast();
if (route.name === "serp-finder") {
  showStopwords.value = false;
}
onMounted(async () => {
  userStore.setIsLoading(!1);
  loadSupportedInfo();
  const { data } = await ApiRequest().get(`/api/serp/projects`);
  const { results = {} } = data;
  projects.value = results;
});
const onClear = () => {
  location.value = null;
};
const getDiffClass = (difficult) => {
  if (difficult.avg_difficulty_diff_text) {
    return difficult.avg_difficulty_diff_text
      .toLowerCase()
      .split(" ")
      .join("-");
  }
  return "";
};
const fetchDifficulty = async () => {
  try {
    difficult.value = null;
    showDifficultyErrorMessage.value = false;
    let url = "";
    if (query.value) {
      url = `api/difficulty/fetch_difficulty?query=${query.value}`;
      const res = await ApiRequest().get(url);
      if (res.status === 200) {
        difficult.value = res.data;
      }
    }
  } catch (error) {
    showDifficultyErrorMessage.value = true;
    console.error("fetch difficulty error", error);
  }
};
const setActiveTab = (tab) => {
  activeTab.value = tab;
};
const handleContinue = () => {
  isShowWizardModalAiArticleCoPilot.value = false;
};
const handleCancel = () => {
  isAiArticleCoPilot.value = false;
  isShowWizardModalAiArticleCoPilot.value = false;
};
const fetchLocations = async (searchQuery) => {
  try {
    const { data } = await ApiRequest().get(
      `https://api.valueserp.com/locations?api_key=demo&q=${searchQuery}`
    );
    let locations = data && data.locations ? data.locations : [];
    if (locations.length) {
      locations = locations.map((l) => {
        return { ...l, full_name: l.full_name.split(",").join(", ") };
      });
    }
    return locations;
  } catch (err) {
    console.error("get locations error", err);
  }
};
const postKeywordDifficulty = async () => {
  const payload = {
    query: query.value,
    location: location.value.full_name,
  };
  try {
    await ApiRequest().post(`/api/difficulty/`, [payload]);
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    emit("submit", extractedError, null);
  }
};
const serpSubmit = async () => {
  const payload = {};
  performTest.value.forEach((test) => {
    payload[test.key] = test.value;
  });
  try {
    if (!query.value) {
      throw new Error("Please enter search query");
    }
    if (!location.value) {
      throw new Error("Please select a location");
    }
    if (trackPositionRanking.value && !trackPositionURL.value) {
      throw new Error("Please enter a URL to track position rankings.");
    }
    userStore.setIsLoading(!0);
    const postData: CreateSearch = {
      ...payload,
      query: query.value,
      limit: limit.value,
      type: props.type,
      speed_threshold: selectedSpeedThreshold.value,
      google_domain: googleDomain.value,
      location: location.value ? location.value.full_name : "",
      country: googleCountry.value,
      language: googleLanguage.value,
      track_url_status:
        trackPositionRanking.value && trackPositionURL.value ? 20 : 10,
      ...(trackPositionRanking.value
        ? { track_url: trackPositionURL.value }
        : {}),
      ...(selectedProject.value ? { project_uuid: selectedProject.value } : {}),
      ...(isAiArticleCoPilot.value
        ? {
            editor_prompt: {
              topic_of_article: topicOfArticle.value,
              use_custom_knowledge_base: useCustomKnowledgeBase.value,
              template_instructions: instructionsOfArticle.value,
              generate_url: articleSelectedPrompts.value.includes("url"),
              generate_title: articleSelectedPrompts.value.includes("title"),
              generate_meta_description:
                articleSelectedPrompts.value.includes("md"),
              generate_article: true,
            },
          }
        : {}),
    };
    if (
      instructionsOfArticle.value ===
        "It is recommended to keep this text, OR give page style/type instructions here" ||
      instructionsOfArticle.value === ""
    ) {
      delete postData.editor_prompt.template_instructions;
    }
    if (showStopwords.value) {
      postData.stopwords = selectedStopwords.value;
    }
    if (!difficult.value && showDifficultyErrorMessage.value) {
      postKeywordDifficulty();
    }
    const { data } = await ApiRequest().post<PostSearchData>(
      `/api/serp/search`,
      postData
    );
    if (data.uuid) {
      userStore.setIsLoading(!1);
      isAiArticleCoPilot.value = false;
      emit("submit", null, data.uuid);
    } else {
      throw new Error("Something went wrong.");
    }
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    if (
      extractedError.alert.message ===
      "You've exhausted your monthly quota. Please upgrade for more credits"
    ) {
      router.push({
        name: "account-subscription",
      });
    }
    emit("submit", extractedError, null);
  } finally {
    userStore.setIsLoading(!1);
  }
};
const serpSubmitBulk = async () => {
  const payload = {};
  performTest.value.forEach((test) => {
    payload[test.key] = test.value;
  });
  try {
    if (!bulkQueries.value) {
      throw new Error("Please enter bulk search query");
    }
    const queries = bulkQueries.value.trim().split(/\n/);
    if (!queries.length) {
      throw new Error("Please enter bulk search query");
    }
    if (queries.length > 10) {
      throw new Error("Max. 10 Keyword/Exact Match Queries are allowed.");
    }
    if (!location.value) {
      throw new Error("Please select a location");
    }
    if (trackPositionRanking.value && !trackPositionURL.value) {
      throw new Error("Please enter a URL to track position rankings.");
    }
    userStore.setIsLoading(!0);
    const postData: CreateSearch[] = [];
    for (const query of queries) {
      const newSearch: CreateSearch = {
        ...payload,
        query: query,
        limit: limit.value,
        type: props.type,
        speed_threshold: selectedSpeedThreshold.value,
        google_domain: googleDomain.value,
        location: location.value ? location.value.full_name : "",
        country: googleCountry.value,
        language: googleLanguage.value,
        track_url_status:
          trackPositionRanking.value && trackPositionURL.value ? 20 : 10,
        ...(trackPositionRanking.value
          ? { track_url: trackPositionURL.value }
          : {}),
        ...(selectedProject.value
          ? { project_uuid: selectedProject.value }
          : {}),
        ...(isAiArticleCoPilot.value
          ? {
              editor_prompt: {
                topic_of_article: topicOfArticle.value,
                use_custom_knowledge_base: useCustomKnowledgeBase.value,
                template_instructions: instructionsOfArticle.value,
                generate_url: articleSelectedPrompts.value.includes("url"),
                generate_title: articleSelectedPrompts.value.includes("title"),
                generate_meta_description:
                  articleSelectedPrompts.value.includes("md"),
                generate_article: true,
              },
            }
          : {}),
      };
      if (
        instructionsOfArticle.value ===
          "It is recommended to keep this text, OR give page style/type instructions here" ||
        instructionsOfArticle.value === ""
      ) {
        delete newSearch.editor_prompt.template_instructions;
      }
      if (showStopwords.value) {
        newSearch.stopwords = selectedStopwords.value;
      }
      postData.push(newSearch);
    }
    const { status } = await ApiRequest().post<PostSearchData[]>(
      `/api/serp/search/spy_bulk_search`,
      postData
    );
    if (status !== 201) {
      throw new Error("Something went wrong");
    }
    router.push({ name: "reports" });
  } catch (error) {
    const extractedError = helper.extractAlert(error);
    emit("submit", extractedError, null);
  } finally {
    userStore.setIsLoading(!1);
  }
};
const handleOpenOptionList = () => {
  location.value = null;
};
const handleLocationChange = (value) => {
  if (value && !isEmpty(value)) {
    const google_domain = domainDetails.value.find(
      (i) => i.main_country.toLowerCase() === value.country_code.toLowerCase()
    );
    googleCountry.value =
      (google_domain && google_domain.country_code) || value.country_code;
    googleDomain.value = (google_domain && google_domain.domain) || "";
    googleLanguage.value = (google_domain && google_domain.language_code) || "";
  } else {
    googleCountry.value = "";
    googleDomain.value = "";
    googleLanguage.value = "";
  }
};
const loadSupportedInfo = async () => {
  const {
    extractedError,
    countries: apiCountries,
    languages: apiLanguages,
    domain_details,
  } = await userStore.getSupportedInfo();
  if (extractedError) {
    userStore.setIsLoading(!1);
    $toast.error(extractedError.alert.message);
    if (extractedError.redirect) {
      userStore.signOut();
      router.push({ name: extractedError.redirect });
    }
  }
  countries.value = apiCountries;
  languages.value = apiLanguages;
  domainDetails.value = domain_details;
  domainDetailsList.value = domainDetails.value
    ? domainDetails.value.filter((obj, index, arr) => {
        return (
          arr.findIndex((innerObj) => innerObj.domain === obj.domain) === index
        );
      })
    : [];
  getDomainDetailsList("", domainDetailsList.value);
};
const getDomainDetailsList = (q, options) => {
  let lists = options;
  if (lists) {
    const matchedEntry = lists.find((list) => list.domain === "google.com");
    if (matchedEntry) {
      lists = [matchedEntry, ...lists.filter((d) => d.domain !== "google.com")];
    }
    domainDetailsList.value = lists;
  }
};
</script>
<style src="@vueform/multiselect/themes/default.scss"></style>
